const SEARCH_MATCH_HIGHLIGHT = "inbox-search-match";

const FRAME_STYLE_ID = `${SEARCH_MATCH_HIGHLIGHT}-style`;

const frameStyle = (source: Document) => {
  const tokens = source.defaultView?.getComputedStyle(source.documentElement);
  const token = (name: string) => tokens?.getPropertyValue(name).trim();
  return `::highlight(${SEARCH_MATCH_HIGHLIGHT}) { background-color: ${token("--primary-20")}; color: ${token("--primary-500")}; }`;
};

type HighlightWindow = Window & {
  CSS?: { highlights?: HighlightRegistry };
  Highlight?: typeof Highlight;
};

const findRanges = (root: Node, needle: string) => {
  const ranges: Range[] = [];
  const doc = root.ownerDocument ?? (root as Document);
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = (node.textContent ?? "").toLowerCase();
    for (
      let index = text.indexOf(needle);
      index !== -1;
      index = text.indexOf(needle, index + needle.length)
    ) {
      const range = doc.createRange();
      range.setStart(node, index);
      range.setEnd(node, index + needle.length);
      ranges.push(range);
    }
  }
  return ranges;
};

const register = (view: HighlightWindow | null, ranges: Range[]) => {
  const registry = view?.CSS?.highlights;
  const HighlightSet = view?.Highlight;
  if (!registry || !HighlightSet || ranges.length === 0) return null;

  registry.set(SEARCH_MATCH_HIGHLIGHT, new HighlightSet(...ranges));
  return () => {
    registry.delete(SEARCH_MATCH_HIGHLIGHT);
  };
};

const highlightFrame = (frame: HTMLIFrameElement, needle: string) => {
  const doc = frame.contentDocument;
  if (!doc?.body) return null;

  if (!doc.getElementById(FRAME_STYLE_ID)) {
    const style = doc.createElement("style");
    style.id = FRAME_STYLE_ID;
    style.textContent = frameStyle(frame.ownerDocument);
    doc.head.appendChild(style);
  }
  return register(
    frame.contentWindow as HighlightWindow | null,
    findRanges(doc.body, needle)
  );
};

const watchFrame = (frame: HTMLIFrameElement, needle: string) => {
  let clear = highlightFrame(frame, needle);
  const rehighlight = () => {
    clear?.();
    clear = highlightFrame(frame, needle);
  };
  frame.addEventListener("load", rehighlight);
  return () => {
    frame.removeEventListener("load", rehighlight);
    clear?.();
  };
};

export const highlightWords = (root: HTMLElement, query: string) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return () => undefined;

  const clears = [
    register(
      root.ownerDocument.defaultView as HighlightWindow | null,
      findRanges(root, needle)
    ),
    ...Array.from(root.querySelectorAll("iframe")).map((frame) =>
      watchFrame(frame, needle)
    )
  ];

  return () => clears.forEach((clear) => clear?.());
};
